// Inloggningen sparas i localStorage, så att den gäller i alla flikar och
// finns kvar när webbläsaren startas om, tills användaren loggar ut eller
// refresh-token går ut (ADR 0109). Går det inte att spara, till exempel i
// privat läge, gäller inloggningen bara tills sidan laddas om. Med Cognito
// sparas också refresh-token och när access-token går ut (ADR 0083).
//
// Webbläsarens ihågkomna enheter (ADR 0108) finns kvar också efter
// utloggning, så att koden inte behövs igen på 30 dagar.
import type { Enhet, Enheter } from '@leadledger/cognito';

const NYCKEL = 'leadledger.konto.token';
const FORNYA = 'leadledger.konto.fornya';
const UTGAR = 'leadledger.konto.utgar';
const ENHET = 'leadledger.konto.enhet';
const ENHETER = 'leadledger.konto.enheter';

function las(nyckel: string): string | null {
  try {
    return localStorage.getItem(nyckel);
  } catch {
    return null;
  }
}

function skriv(nyckel: string, varde: string | null | undefined) {
  try {
    if (varde) localStorage.setItem(nyckel, varde);
    else localStorage.removeItem(nyckel);
  } catch {
    // Inloggningen gäller ändå i den här sidvisningen.
  }
}

/** Nyckeln för token, så att kontot märker när en annan flik loggar in eller ut. */
export const TOKEN_NYCKEL = NYCKEL;

export function lasToken(): string | null {
  return las(NYCKEL);
}

export function sparaToken(token: string | null) {
  skriv(NYCKEL, token);
  if (!token) {
    skriv(FORNYA, null);
    skriv(UTGAR, null);
    skriv(ENHET, null);
  }
}

/** Refresh-token, när access-token går ut och enheten, om inloggningen kom från Cognito. */
export function lasFornyelse(): {
  refreshToken: string;
  expiresAt: number;
  deviceKey?: string;
} | null {
  const refreshToken = las(FORNYA);
  const expiresAt = Number(las(UTGAR));
  const deviceKey = las(ENHET);
  return refreshToken && Number.isFinite(expiresAt)
    ? { refreshToken, expiresAt, ...(deviceKey ? { deviceKey } : {}) }
    : null;
}

/** En ny refresh-token sparas med sin enhet. Utan refresh-token ändras bara tiden. */
export function sparaFornyelse(
  refreshToken: string | undefined,
  expiresAt: number,
  deviceKey?: string,
) {
  if (refreshToken) {
    skriv(FORNYA, refreshToken);
    skriv(ENHET, deviceKey);
  }
  skriv(UTGAR, String(expiresAt));
}

function lasEnheter(): Record<string, Enhet> {
  try {
    const data = JSON.parse(localStorage.getItem(ENHETER) ?? '{}') as unknown;
    return data && typeof data === 'object' ? (data as Record<string, Enhet>) : {};
  } catch {
    return {};
  }
}

/** Webbläsarens enheter, en per e-postadress. Går det inte att spara frågar Cognito efter koden. */
export const webblasarensEnheter: Enheter = {
  las: (epost) => lasEnheter()[epost.trim().toLowerCase()] ?? null,
  spara: (epost, enhet) => {
    const nyckel = epost.trim().toLowerCase();
    const alla = Object.fromEntries(Object.entries(lasEnheter()).filter(([e]) => e !== nyckel));
    if (enhet) alla[nyckel] = enhet;
    try {
      localStorage.setItem(ENHETER, JSON.stringify(alla));
    } catch {
      // Då frågar Cognito efter koden nästa gång, som förut.
    }
  },
};
